import React, { useEffect, useState } from 'react';
import { listSubmissions, getStats, getAnnotatorStats, getMyQueue, lookupSubmission, QueueCategory } from '../api/client';

const CGC_GRADES = [10.0, 9.8, 9.6, 9.4, 9.2, 9.0, 8.5, 8.0, 7.5, 7.0, 6.5,
  6.0, 5.5, 5.0, 4.5, 4.0, 3.5, 3.0, 2.5, 2.0, 1.8, 1.5, 1.0, 0.5];

interface Submission {
  submission_id: string;
  status: string;
  created_at: string;
  ai_grade: number;
  ai_label: string;
  ai_confidence: string;
  user_counter_grade?: number;
  photo_count: number;
}

interface Stats {
  counts: { pending_review: number; reviewed: number; exported: number };
}

interface Annotator {
  user_id: string;
  nickname: string;
  count: number;
  last_active: string;
}

interface Props {
  onReview: (id: string) => void;
}

export default function Dashboard({ onReview }: Props) {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [annotators, setAnnotators] = useState<Annotator[]>([]);
  const [queueMode, setQueueMode] = useState<'queue' | 'browse' | 'missed'>(
    () => (localStorage.getItem('dash_queueMode') as any) || 'queue'
  );
  const [category, setCategory] = useState<QueueCategory>(
    () => (localStorage.getItem('dash_category') as QueueCategory) || 'big_dispute'
  );
  const [statusFilter, setStatusFilter] = useState(
    () => localStorage.getItem('dash_statusFilter') || 'PENDING_REVIEW'
  );
  const [fromDate, setFromDate] = useState(
    () => localStorage.getItem('dash_fromDate') || ''
  );
  const [toDate, setToDate] = useState(
    () => localStorage.getItem('dash_toDate') || ''
  );

  useEffect(() => { localStorage.setItem('dash_queueMode', queueMode); }, [queueMode]);
  useEffect(() => { localStorage.setItem('dash_category', category); }, [category]);
  useEffect(() => { localStorage.setItem('dash_statusFilter', statusFilter); }, [statusFilter]);
  useEffect(() => { localStorage.setItem('dash_fromDate', fromDate); }, [fromDate]);
  useEffect(() => { localStorage.setItem('dash_toDate', toDate); }, [toDate]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const [subResp, statsResp, annotatorResp] = await Promise.all([
          queueMode === 'queue' ? getMyQueue(category, fromDate, toDate)
            : queueMode === 'missed' ? listSubmissions('MISSED', 50, fromDate, toDate)
            : listSubmissions(statusFilter, 50, fromDate, toDate),
          getStats(),
          getAnnotatorStats(),
        ]);
        setSubmissions(subResp.submissions ?? []);
        setStats(statsResp);
        setAnnotators(annotatorResp.annotators ?? []);
        setLoading(false);
        return;
      } catch (e: any) {
        // Retry once on network-level failures (stale HTTP/2 connections from CloudFront)
        if (attempt === 0 && e instanceof TypeError) continue;
        setError(e.message);
        break;
      }
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [queueMode, category, statusFilter, fromDate, toDate]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    setSearching(true);
    setSearchError(null);
    try {
      const result = await lookupSubmission(q);
      onReview(result.submission_id);
    } catch (e: any) {
      setSearchError(e.message === 'Not found' ? 'No submission found for that ID or cert number.' : e.message);
    } finally {
      setSearching(false);
    }
  };

  const statusLabel = (s: Submission) => {
    const diff = s.user_counter_grade != null && s.ai_grade != null
      ? Math.abs(s.user_counter_grade - s.ai_grade)
      : null;
    const diverged = diff != null && diff >= 0.5;
    return (
      <span>
        {s.status === 'PENDING_REVIEW' ? (
          <span className="badge badge-pending">Pending Review</span>
        ) : s.status === 'REVIEWED' ? (
          <span className="badge badge-reviewed">Reviewed</span>
        ) : s.status === 'MISSED' ? (
          <span className="badge badge-missed">Missed</span>
        ) : (
          <span className="badge badge-exported">Exported</span>
        )}
        {diverged && (
          <span className="badge badge-diverged" title={`User said ${s.user_counter_grade}, AI said ${s.ai_grade}`}>
            Grade Dispute ({diff!.toFixed(1)} pts)
          </span>
        )}
      </span>
    );
  };

  return (
    <div className="dashboard">
      {stats && (
        <div className="stats-bar">
          <div className="stat-card">
            <span className="stat-num">{stats.counts.pending_review}</span>
            <span className="stat-label">Pending Review</span>
          </div>
          <div className="stat-card">
            <span className="stat-num">{stats.counts.reviewed}</span>
            <span className="stat-label">Reviewed</span>
          </div>
          <div className="stat-card">
            <span className="stat-num">{stats.counts.exported}</span>
            <span className="stat-label">Exported to Training</span>
          </div>
        </div>
      )}

      {annotators.length > 0 && (
        <div className="leaderboard">
          <h3 className="leaderboard-title">Grader Leaderboard</h3>
          <div className="leaderboard-list">
            {annotators.map((a, i) => (
              <div key={a.user_id} className="leaderboard-row">
                <span className={`leaderboard-rank rank-${i + 1}`}>#{i + 1}</span>
                <span className="leaderboard-name">{a.nickname}</span>
                <span className="leaderboard-count">{a.count} {a.count === 1 ? 'grade' : 'grades'}</span>
                {a.last_active && (
                  <span className="leaderboard-date">last active {a.last_active}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="queue-tabs-row">
        <div className="queue-tabs">
          <button
            className={`queue-tab ${queueMode === 'queue' && category === 'big_dispute' ? 'queue-tab-active queue-tab-hot' : ''}`}
            onClick={() => { setQueueMode('queue'); setCategory('big_dispute'); }}
          >
            <span className="queue-tab-label">Big Disputes</span>
            <span className="queue-tab-sub">≥ 2.0 pts apart</span>
          </button>
          <button
            className={`queue-tab ${queueMode === 'queue' && category === 'disputed' ? 'queue-tab-active' : ''}`}
            onClick={() => { setQueueMode('queue'); setCategory('disputed'); }}
          >
            <span className="queue-tab-label">Disputed</span>
            <span className="queue-tab-sub">1.0 – 1.9 pts apart</span>
          </button>
          <button
            className={`queue-tab ${queueMode === 'queue' && category === 'close' ? 'queue-tab-active' : ''}`}
            onClick={() => { setQueueMode('queue'); setCategory('close'); }}
          >
            <span className="queue-tab-label">Close</span>
            <span className="queue-tab-sub">≤ 0.5 pts apart</span>
          </button>
          <button
            className={`queue-tab ${queueMode === 'queue' && category === 'exact' ? 'queue-tab-active' : ''}`}
            onClick={() => { setQueueMode('queue'); setCategory('exact'); }}
          >
            <span className="queue-tab-label">Exact Match</span>
            <span className="queue-tab-sub">User agreed exactly</span>
          </button>
          <button
            className={`queue-tab ${queueMode === 'queue' && category === 'all' ? 'queue-tab-active' : ''}`}
            onClick={() => { setQueueMode('queue'); setCategory('all'); }}
          >
            <span className="queue-tab-label">All</span>
            <span className="queue-tab-sub">Oldest first</span>
          </button>
          <button
            className={`queue-tab ${queueMode === 'missed' ? 'queue-tab-active' : ''}`}
            onClick={() => setQueueMode('missed')}
          >
            <span className="queue-tab-label">Missed</span>
            <span className="queue-tab-sub">Pre-July archive</span>
          </button>
          <button
            className={`queue-tab ${queueMode === 'browse' ? 'queue-tab-active' : ''}`}
            onClick={() => setQueueMode('browse')}
          >
            <span className="queue-tab-label">Browse All</span>
            <span className="queue-tab-sub">By status</span>
          </button>
        </div>
        <div className="queue-tabs-right">
          <form className="submission-search" onSubmit={handleSearch}>
            <input
              type="text"
              className="submission-search-input"
              placeholder="Cert # or sub-ID…"
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setSearchError(null); }}
            />
            <button type="submit" className="submission-search-btn" disabled={searching}>
              {searching ? '…' : 'Go'}
            </button>
          </form>
          {searchError && <span className="submission-search-error">{searchError}</span>}
          {queueMode === 'browse' && (
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="PENDING_REVIEW">Pending Review</option>
              <option value="REVIEWED">Reviewed</option>
              <option value="EXPORTED">Exported</option>
            </select>
          )}
          <div className="date-filter-row">
            <label className="date-filter-label">From</label>
            <input
              type="date"
              className="date-filter-input"
              value={fromDate}
              onChange={e => setFromDate(e.target.value)}
            />
            <label className="date-filter-label">To</label>
            <input
              type="date"
              className="date-filter-input"
              value={toDate}
              onChange={e => setToDate(e.target.value)}
            />
            {(fromDate || toDate) && (
              <button className="date-filter-clear" onClick={() => { setFromDate(''); setToDate(''); }}>✕</button>
            )}
          </div>
          <button className="refresh-btn" onClick={load} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {!loading && submissions.length === 0 && (
        <div className="empty-state">
          {queueMode === 'missed' ? 'No missed submissions.'
            : queueMode === 'browse' ? 'No submissions in this status.'
            : "You're all caught up in this category!"}
        </div>
      )}

      <div className="submissions-list">
        {submissions.map(sub => (
          <div key={sub.submission_id} className="submission-card">
            <div className="submission-meta">
              <span className="submission-id">{sub.submission_id}</span>
              <span className="submission-date">
                {new Date(sub.created_at).toLocaleDateString()}
              </span>
              {statusLabel(sub)}
            </div>
            <div className="submission-grades">
              <div className="grade-box">
                <span className="grade-source">AI Grade</span>
                <span className={`grade-value grade-${Math.floor(sub.ai_grade ?? 0)}`}>
                  {sub.ai_grade != null ? sub.ai_grade.toFixed(1) : 'N/A'}
                </span>
                <span className="grade-label">{sub.ai_label}</span>
              </div>
              {(sub as any).user_grade_guess != null && (
                <>
                  <div className="divergence-arrow">→</div>
                  <div className="grade-box">
                    <span className="grade-source">User Thought</span>
                    <span className="grade-value">{((sub as any).user_grade_guess as number).toFixed(1)}</span>
                    {sub.ai_grade != null && (
                      <span className={`divergence-chip ${Math.abs(((sub as any).user_grade_guess as number) - sub.ai_grade) >= 2 ? 'chip-hot' : Math.abs(((sub as any).user_grade_guess as number) - sub.ai_grade) >= 1 ? 'chip-warn' : 'chip-ok'}`}>
                        {Math.abs(((sub as any).user_grade_guess as number) - sub.ai_grade) === 0
                          ? '✓ match'
                          : `${Math.abs(((sub as any).user_grade_guess as number) - sub.ai_grade).toFixed(1)} pt gap`}
                      </span>
                    )}
                  </div>
                </>
              )}
              <div className="photo-count">{sub.photo_count} photos</div>
            </div>
            <button className="review-btn" onClick={() => onReview(sub.submission_id)}>
              {sub.status === 'PENDING_REVIEW' ? 'Review & Annotate' : 'View / Edit'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
